import _ from 'lodash';

import {
  Artifact,
  ArtifactCollection,
  ArtifactSequence,
  useFetchJobDetailsQuery,
} from '../../../generated/graphql';
import {AccessOptions} from '../../../util/permissions';
import {getArtifactInfo, getVersionInfo} from '../Jobs/jobUtil';
import {convertArtifactToVersion, extractRuns} from './utilData';
import {JobDetails, JobRuns} from './utilTypes';

// Get a list of unique top-level config keys from a list of runs.
const getConfigKeys = (runs: JobRuns): string[] => {
  const configKeys: string[] = [];
  for (const run of runs) {
    for (const key in run.config) {
      if (!key.startsWith('_') && !configKeys.includes(key)) {
        configKeys.push(key);
      }
    }
  }
  return configKeys;
};

export const useJobDetailsNoPoll = (jobId: string) => {
  const {data, loading, error} = useFetchJobDetailsQuery({
    variables: {
      jobId,
    },
    skip: !jobId,
  });
  const job = getJobDetails(data?.artifactCollection as ArtifactSequence);
  if (loading || error || !data) {
    return {job: null, loading, error};
  }
  return {
    job,
    loading,
    error,
  };
};

export const useJobDetails = (jobId: string) => {
  const {data, loading, error} = useFetchJobDetailsQuery({
    variables: {
      jobId,
    },
    pollInterval: 5000,
    skip: !jobId,
  });
  const job = getJobDetails(data?.artifactCollection as ArtifactSequence);

  if (loading || error || !data) {
    return {job: null, loading, error};
  }
  return {
    job,
    loading,
    error,
  };
};

export const getJobDetails = (ac: ArtifactCollection | ArtifactSequence) => {
  const name = ac?.name ?? '';
  const description = ac?.description ?? '';
  const projectName = ac?.project.name;
  const projectAccess = (ac?.project.access ?? 'PRIVATE') as AccessOptions;
  const createdAt = ac?.createdAt.toString() ?? '';

  const artifactEdges = ac?.artifacts?.edges ?? [];
  const artifacts = artifactEdges.map(edge => edge.node as Artifact);

  const versions = artifacts.map(artifact =>
    convertArtifactToVersion(artifact)
  );

  // Get information such as run target.
  const artifactInfo = getArtifactInfo(artifactEdges as any);
  const versionInfo = getVersionInfo(artifactInfo);

  // Aggregate run information from all versions of the job and sort.
  const aggregatedRuns: JobRuns = [];
  artifacts.forEach(artifact => aggregatedRuns.push(...extractRuns(artifact)));
  const runs = _.orderBy(aggregatedRuns, ['createdAt'], ['desc']);
  const configKeys = getConfigKeys(runs);

  const job: JobDetails = {
    id: ac?.id,
    name,
    projectName,
    description,
    createdAt,
    projectAccess,
    versions,
    versionInfo,
    runs,
    configKeys,
  };

  return job;
};
